import React from 'react';

import { Box, Separator, Shimmer } from '@betterment/samba-react';

type WidthValue = string | ((rowIndex: number) => string);

type ColumnConfig = {
  width: WidthValue;
  secondWidth?: WidthValue;
  align?: 'left' | 'right';
};

interface TableSkeletonProps {
  rowCount?: number;
  columns?: ColumnConfig[];
}

const resolveWidth = (value: WidthValue | undefined, rowIndex: number) =>
  typeof value === 'function' ? value(rowIndex) : value;

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { width: '30%' },
  { width: '90%', secondWidth: '60%' },
  { width: '75%' },
  { width: '40%' },
];

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rowCount = 5, columns = DEFAULT_COLUMNS }) => {
  return (
    <>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <Box
          key={rowIndex} // eslint-disable-line react/no-array-index-key
        >
          <Box
            css={{
              px: 'space.300',
              py: 'space.200',
              display: 'grid',
              gap: 'space.200',
              gridTemplateColumns: '1fr 1fr 2fr 1fr',
              alignItems: 'center',
            }}
          >
            {columns.map((col, colIndex) => {
              const mainWidth = resolveWidth(col.width, rowIndex);
              const secondWidth = resolveWidth(col.secondWidth, rowIndex);

              return (
                <Box
                  key={colIndex} // eslint-disable-line react/no-array-index-key
                >
                  <Box
                    css={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'space.100',
                      py: 'space.100',
                      alignItems: colIndex === 3 ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Shimmer
                      css={{
                        width: mainWidth,
                        height: '16px',
                        borderRadius: 'medium',
                      }}
                    />
                    {secondWidth && (
                      <Shimmer
                        css={{
                          width: secondWidth,
                          height: '12px',
                          borderRadius: 'medium',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
        </Box>
      ))}
    </>
  );
};
